import { useMutation } from '@tanstack/react-query';
import { useRequestSections } from '../../../api/request';
import { useCallback } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../redux/Store';
import {
  addToCloneQueue,
  QueueFileData,
  removeFromCloneQueue,
  setUploadedGalleryImages,
  deleteUploadedGalleryImage,
} from '../../../redux/reducer/galleryReducer';

import { setShow403Dialog } from '../../../redux/reducer/forbiddenDetectorReducer';
import { useAppSelector } from '../../../redux/Store';
import { addMediaLabelToImage } from '../../../redux/reducer/coreScriptReducer';
import { Media } from '@safeguardproperties/corescript-react-npm/dist/types';
import customNotifications from './customNotifications';
import { ImageMetadata } from '../../../types/ui';
import { ImageStatus } from '../../../constants';

import useQueryObject from '../../../hooks/useQueryObject';

const useImages = () => {
  const { ImagesApi } = useRequestSections();
  const contextId = useAppSelector((state) => state.contextId.contextId);
  const { orderNumber } = useQueryObject();
  const dispatch = useDispatch<AppDispatch>();

  const {
    mutateAsync: fetchImages,
    data: imagesData,
    isError: imagesDataError,
  } = useMutation({
    mutationFn: async () => {
      const response = await ImagesApi?.getImages(contextId);
      if (response?.data) {
        const values =
          (Object.values(response?.data?.images) as ImageMetadata[]) || [];
        dispatch(setUploadedGalleryImages(values));
      }
      return response?.data;
    },
    onError: (error: any) => {
      const errorCode =
        error?.response?.status || error?.code || 'Unknown Error';
      customNotifications('Get images', errorCode);
    },
    retry: 3,
  });

  const {
    mutateAsync: postOrderImage,
    isPending: isPostOrderImagePending,
    error: postOrderImageError,
    isSuccess: successfulPostOrderImage,
    data: postOrderImageData,
  } = useMutation({
    mutationFn: async (media: QueueFileData) => {
      const parameters = {
        image_type_id: 5,
        correlation_id: media.guid,
        category: 'Unknown',
        prefix: 'Unknown',
        description: '',
        order_number: orderNumber, //TODO in future may need to support different contextid that are not order number maybe postPropertyImage...
        department_code: 'A',
      };
      const response = await ImagesApi?.postOrderImage(parameters);
      return {
        id: response?.data?.id,
        postUrl: response?.data?.postUrl,
      };
    },
    onError: (error: any) => {
      const errorCode =
        error?.response?.status || error?.code || 'Unknown Error';
      customNotifications('Post order image', errorCode);
    },
  });

  const {
    mutateAsync: uploadImageToS3,
    isPending: isUploadToS3Pending,
    error: uploadToS3Error,
    isSuccess: successfulUploadToS3,
  } = useMutation({
    mutationFn: async ({ file, url }: { file: File; url: string }) => {
      const response = await axios.put(url, file, {
        headers: {
          'Content-Type': file.type, // Use the file's MIME type
          'Content-Length': file.size.toString(), // Optional: Set the file size
        },
      });
      return response.data;
    },
    onError: (error: any) => {
      const errorCode =
        error?.response?.status || error?.code || 'Unknown Error';
      customNotifications('Upload image', errorCode);
    },
  });

  const {
    mutateAsync: checkImageStatusById,
    isPending: isCheckingImageStatus,
    error: checkStatusError,
    isSuccess: checkStatusSuccess,
  } = useMutation({
    mutationFn: async (guid: string) => {
      const payload = {
        images: [guid],
      };
      const response = await ImagesApi?.checkImageStatus(payload);
      return response;
    },
    onError: (error: any) => {
      const errorCode =
        error?.response?.status || error?.code || 'Unknown Error';
      customNotifications('Image status', errorCode);
    },
  });

  const uploadImageSequence = useCallback(
    async (media: QueueFileData): Promise<any> => {
      try {
        const postResponse = await postOrderImage(media);
        const uploadUrl = postResponse.postUrl;
        const imageId = postResponse.id;
        await uploadImageToS3({ url: uploadUrl, file: media.file });
        await new Promise((resolve) => setTimeout(resolve, 2000));
        let statusResponse = await checkImageStatusById(imageId);
        let attempts = 0;
        while (
          (statusResponse?.data?.images[0]?.status ===
            ImageStatus.IMAGE_STATE_UPLOADED ||
            statusResponse?.data?.images[0]?.status ===
              ImageStatus.IMAGE_STATE_CREATED) &&
          attempts < 5
        ) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          statusResponse = await checkImageStatusById(imageId);
          attempts++;
        }
        // .

        return statusResponse?.data;
      } catch (error) {
        console.error('error logged for the image sequence', error);
        throw error;
      }
    },
    [postOrderImage, uploadImageToS3, checkImageStatusById]
  );

  const startImageUploadProcess = useCallback(
    async (media: QueueFileData): Promise<any> => {
      try {
        const result = await uploadImageSequence(media);
        const imageStatusResponse = result?.images[0];

        if (
          imageStatusResponse?.status === ImageStatus.IMAGE_STATE_SUCCESS ||
          imageStatusResponse?.status === ImageStatus.IMAGE_STATE_UPLOADED ||
          imageStatusResponse?.status === ImageStatus.IMAGE_STATE_REJECT ||
          imageStatusResponse?.status === ImageStatus.IMAGE_STATE_CREATED
        ) {
          return imageStatusResponse;
        }

        throw new Error(
          'Image upload failed with status: ' + imageStatusResponse?.status
        );
      } catch (error) {
        console.error('Error during image upload', error);
        throw error;
      }
    },
    [uploadImageSequence]
  );

  const processImageDeleteRequest = useCallback(
    async (media: ImageMetadata): Promise<any> => {
      try {
        const response = await ImagesApi?.deleteOrderImage({
          image_uuid: media.guid,
          order_number: media.orderNumber,
        });
        dispatch(deleteUploadedGalleryImage(media));
        return {
          image_uuid: response?.data?.image_uuid,
          order_number: response?.data?.order_number,
        };
      } catch (error: any) {
        throw new Error(error);
      }
    },
    [ImagesApi, dispatch]
  );

  const processImageCloneRequest = useCallback(
    async (label: string, guids: string[]) => {
      if (!label) return;

      try {
        dispatch(addToCloneQueue(label));
        const request = {
          media: {
            label,
            guids,
          },
        };

        const response = await ImagesApi?.cloneImage(contextId, request);
        const mediaArray = [...(response?.data?.media || [])];

        if (mediaArray.length > 0) {
          const media = mediaArray.map(
            (item) =>
              ({
                Guid: item.imageGuid,
                ThumbUri: item.webFileName,
              }) as Media
          );

          dispatch(addMediaLabelToImage({ label, media }));
        } else {
          throw new Error('Files not returned from the clone operation');
        }
      } catch (error: any) {
        throw new Error(error);
      } finally {
        dispatch(removeFromCloneQueue(label));
      }
    },
    [ImagesApi, dispatch, contextId]
  );

  const getMediaMetaDataByGuids = async (guids: string[]) => {
    const data = await ImagesApi.fetchImageMetaByGuid(contextId, {
      guids,
    });
    return data.data;
  };

  const useImage403ErrorHandler = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const imgElement = event.currentTarget as HTMLImageElement;
      const tryFetch = async (attempt = 1): Promise<void> => {
        try {
          const url = new URL(imgElement.src, globalThis.location.origin);
          url.searchParams.set('time', Date.now().toString());

          const response = await fetch(url.toString(), {
            credentials: 'include',
          });
          if (response.ok) {
            // Update the image src to force reload with the new timestamp
            imgElement.src = url.toString();
          } else {
            if (response.status === 403) {
              console.error(`Error ${response.status}: ${response.statusText}`);
              dispatch(setShow403Dialog(true));
              return;
            } else if (attempt < 16) {
              await new Promise((res) => setTimeout(res, 2000));
              return tryFetch(attempt + 1);
            } else {
              console.error(
                `Fetch failed after 5 attempts: ${response.status} ${response.statusText}`
              );
            }
          }
        } catch (error) {
          if (attempt < 5) {
            await new Promise((res) => setTimeout(res, 1000));
            return tryFetch(attempt + 1);
          } else {
            console.error('Fetch failed after 5 attempts:', error);
          }
        }
      };

      tryFetch();
    },
    [dispatch]
  );

  return {
    imagesData,
    imagesDataError,
    fetchImages,

    postOrderImage,
    postOrderImageData,
    postOrderImageError,
    isPostOrderImagePending,
    successfulPostOrderImage,

    uploadImageToS3,
    isUploadToS3Pending,
    uploadToS3Error,
    successfulUploadToS3,

    isCheckingImageStatus,
    checkStatusError,
    checkStatusSuccess,

    startImageUploadProcess,

    processImageCloneRequest,

    processImageDeleteRequest,
    useImage403ErrorHandler,
  };
};

export default useImages;
